import React from 'react';
import { useHistory } from 'react-router-dom';

import { useStyles } from '~/assets/styles';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import type { ClaimSummarized, Communication, EmailDomain } from '~/server_shared/swagger/data-contracts';
import { getLobIcon } from '~/Utils/lobUtils';

import CardDialog from '../CardDialog';
import ClaimLink from '../ClaimLink';
import ClaimSearchContainer from '../ClaimSearch';
import { ContactEntity } from '../Contact';
import { Heading, SortableTable } from '../core';
import SkeletonTable from '../core/Skeletons/SkeletonTable';
import { useCms } from '../hooks/useCms';
import useOrganization from '../OrganizationContext';
import useDataFetcher from '../useDataFetcher';

import { CommunicationAttachToKnownClaimButton } from './CommunicationAttachToKnownClaimButton';
import { isEmailCommunication } from './types';
import ViewCommunicationCardContainer from './ViewCommunicationCardContainer';

type CommunicationAttachExtraParams = {
  contact_id?: number | '';
  should_create_new_contact?: boolean;
};

interface AttachCommunicationPageProps {
  communicationId: number;
}

const AttachCommunicationPage: React.FC<AttachCommunicationPageProps> = ({ communicationId }) => {
  const history = useHistory();

  const handleUpdate = (communication: Communication) => {
    if (communication?.claim_id) {
      history.push(`/claims/${communication.claim_id}/communications`);
    }
  };

  return (
    <div className="m-8" style={{ overflow: 'auto' }}>
      <CardDialog trackAlt={`Communication ${communicationId}`}>
        <ViewCommunicationCardContainer communicationId={communicationId} onUpdate={handleUpdate} />
      </CardDialog>
    </div>
  );
};

interface ClaimSearchAttachButtonProps {
  claim: ClaimSummarized;
  communication: Communication;
  onAttach: (claimId: number) => Promise<void> | void;
  onAttachOverride?: (claimId: number, attachExtraParams: CommunicationAttachExtraParams) => Promise<void> | void;
}

// very bad component design in ClaimSearchContainer, it calls <SelectComponent claim={row} onSelect={onClaimLinkClick} {...selectComponentProps} />
// we use this adapter to comply with the design
const ClaimSearchAttachButton: React.FC<ClaimSearchAttachButtonProps> = ({
  claim,
  communication,
  onAttach,
  onAttachOverride,
}) => (
  <CommunicationAttachToKnownClaimButton
    claimId={claim.id}
    communicationId={communication.id}
    communicationChannel={communication.channel ?? ''}
    onAttach={onAttach}
    onAttachOverride={onAttachOverride}
  />
);

interface AttachCommunicationContainerProps {
  communication: Communication;
  onAttach: (claimId: number) => Promise<void> | void;
  onAttachOverride?: (claimId: number, attachExtraParams: CommunicationAttachExtraParams) => Promise<void> | void;
}

const AttachCommunicationContainer: React.FC<AttachCommunicationContainerProps> = ({
  communication,
  onAttach,
  onAttachOverride,
}) => {
  const { emailDomains } = useOrganization() as {
    emailDomains: EmailDomain[];
  };
  const { userOrganization } = useCms();
  const { classes } = useStyles();

  const {
    isLoading,
    isError,
    data: possibleClaimsToAttach = [],
  } = useDataFetcher<ClaimSummarized[]>(`/api/v1/communications/${communication.id}/possible_claims_to_attach`);
  const { lobConfigurationsDict } = useLobConfiguration();

  const displayLoading = isLoading || isError;

  const possibleClaimsColumns = [
    {
      id: 'type',
      width: '10px',
      specialCell: (claim: ClaimSummarized) => getLobIcon({ lob: claim.lob, lobConfigurationsDict }),
    },
    // stopPropagation from Link, otherwise, Link will be followed but Table onClick will also occur
    {
      id: 'id',
      numeric: false,
      width: '160px',
      disablePadding: false,
      label: 'Claim',
      specialCell: (claim: ClaimSummarized) => <ClaimLink claimId={claim.id} linkText={claim.claim_id_display} />,
      specialCmpFunc: (row1: ClaimSummarized, row2: ClaimSummarized) => (row1.id || 0) - (row2.id || 0),
    },
    {
      id: 'insured_contact_full_name',
      numeric: false,
      disablePadding: false,
      label: 'Named Insured',
      specialCell: (row: ClaimSummarized) =>
        row.insured_contact_id && row.insured_contact_full_name ? (
          <ContactEntity contactId={row.insured_contact_id} contactDisplayName={row.insured_contact_full_name} />
        ) : null,
    },
    {
      id: 'select_button',
      numeric: false,
      label: '',
      specialCell: (row: ClaimSummarized) => (
        <CommunicationAttachToKnownClaimButton
          claimId={row.id}
          communicationId={communication.id}
          communicationChannel={communication.channel}
          onAttach={onAttach}
          onAttachOverride={onAttachOverride}
        />
      ),
    },
  ];

  const emailDomain = isEmailCommunication(communication) ? communication.email_domain : undefined;
  const subOrganizationIds = userOrganization.is_suborgs_domains_enabled
    ? emailDomains.find((domain) => domain.domain === emailDomain)?.sub_organization_ids
    : [];

  return (
    <div className="my-12 rounded-lg border-1 border-solid border-slate-400 bg-slate-100">
      <div className="mb-8 mt-8">
        <ClaimSearchContainer
          classes={classes}
          SelectComponent={ClaimSearchAttachButton}
          selectComponentProps={{
            communication,
            onAttach,
            onAttachOverride,
          }}
          label="Attach to claim"
          subOrganizationIds={subOrganizationIds}
          searchContainerClassName="bg-slate-100"
        />
      </div>

      <div className="mb-8 mt-8">
        <CardDialog noCardTitle containerClassName="bg-slate-100">
          <Heading variant={Heading.TYPES.H4}>May be related to the following claims:</Heading>
          {displayLoading ? (
            <SkeletonTable rowsCount={5} columnsCount={possibleClaimsColumns.length} isError={isError} />
          ) : (
            <SortableTable rows={possibleClaimsToAttach} columns={possibleClaimsColumns} />
          )}
        </CardDialog>
      </div>
    </div>
  );
};

export { AttachCommunicationPage };
export default AttachCommunicationContainer;
